import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import styles from "./style.module.scss";
import PrintPage from "@components/print/PrintElements/PrintPage";
import ReportPage, { ReportDataProps, ReportTitleProps } from "@components/dynamic/ReportPage";
import { useAppSelector } from "@/store/hooks";
import { CategoryApi } from "@/api/CategoryApi";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import {
  ALL_CATEGORIES_LABEL,
  buildReportRows,
  buildReportTitles,
  decodeReportProperties,
  fetchProductsByCategory,
  resolveCategoryLabel
} from "@utils/ProductsByCategoryReport";

type ProductsByCategoryReportPrintProps = {
  excelExport?: boolean
}

const ProductsByCategoryReportPrint = (props: ProductsByCategoryReportPrintProps) => {
  const { excelExport = false } = props
  const params = useParams()
  const { categoryId = "", properties = "", includeInactive = "0", autoPrint = false } = params;
  const suscription = useAppSelector((state) => { return state.suscription.suscription || {} });
  const [reportData, setReportData] = useState<ReportDataProps[]>([]);
  const [reportTitles, setReportTitles] = useState<ReportTitleProps[]>([]);
  const [categoryName, setCategoryName] = useState<string>(ALL_CATEGORIES_LABEL);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    import("@printerVariation/generic.scss");
  }, []);

  useEffect(() => {
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadReportData()
  }, [categoryId, properties, includeInactive]);

  const loadCategoryName = async (): Promise<string> => {
    try {
      const categories = await CategoryApi.optionsList({ activo: { eq: true } });
      return resolveCategoryLabel(categoryId, categories);
    } catch (err) {
      console.log("Not able to load the category name", err)
      return "";
    }
  }

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      const selectedKeys = decodeReportProperties(properties);

      setReportTitles(buildReportTitles(selectedKeys))
      setCategoryName(await loadCategoryName())

      const groups = await fetchProductsByCategory({
        categoryId,
        properties: selectedKeys,
        includeInactive: includeInactive === "1"
      })

      setReportData(buildReportRows(groups, selectedKeys, styles.categoryRow))
    } finally {
      setIsLoading(false);
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  const renderSubtitle = (): string => {
    return includeInactive === "1"
      ? `${categoryName} | Incluye productos inactivos`
      : `${categoryName}`;
  }

  return (
    <div>
      <PrintPage
        autoPrint={autoPrint === "1"}
        onReadyDebounce={2000}
      >
        <ReportPage
          loading={isLoading}
          excelExport={excelExport}
          maxItemPerPage={42}
          suscription={suscription}
          reportConfig={{
            title: "Reporte por Categoria",
            subtitle: renderSubtitle(),
            xmlFileName: "Reporte por Categoria",
            date: `${moment().format()}`,
          }}
          reportData={reportData}
          reportTitles={reportTitles}
        />
      </PrintPage>
    </div>
  );
}

export default ProductsByCategoryReportPrint;
